import {
  OPERATOR_TYPES_1,
  OPERATOR_TYPES_4,
} from 'dashboard/routes/dashboard/settings/automation/operators';

// Extended text operators adding starts_with and ends_with
const OPERATOR_TYPES_TEXT_EXTENDED = [
  { value: 'equal_to', label: 'Equal to' },
  { value: 'not_equal_to', label: 'Not equal to' },
  { value: 'contains', label: 'Contains' },
  { value: 'does_not_contain', label: 'Does not contain' },
  { value: 'is_present', label: 'Is present' },
  { value: 'is_not_present', label: 'Is not present' },
  { value: 'starts_with', label: 'Starts with' },
  { value: 'ends_with', label: 'Ends with' },
];
import {
  DEFAULT_MESSAGE_CREATED_CONDITION,
  DEFAULT_CONVERSATION_CONDITION,
  DEFAULT_OTHER_CONDITION,
  DEFAULT_ACTIONS,
} from 'dashboard/constants/automation';
import actionQueryGenerator from 'dashboard/helper/actionQueryGenerator';
import filterQueryGenerator from './filterQueryGenerator';
import { getActionKeysForEvent } from './event-entity-map';

export const getCustomAttributeInputType = key => {
  const customAttributeMap = {
    date: 'date',
    text: 'plain_text',
    list: 'search_select',
    checkbox: 'search_select',
  };

  return customAttributeMap[key] || 'plain_text';
};

export const isACustomAttribute = (customAttributes, key) => {
  if (!Array.isArray(customAttributes)) {
    return null;
  }
  return customAttributes.find(attr => {
    return attr.attribute_key === key;
  });
};

export const getCustomAttributeListDropdownValues = (
  customAttributes,
  type
) => {
  return customAttributes
    .find(attr => attr.attribute_key === type)
    .attribute_values.map(item => {
      return {
        id: item,
        name: item,
      };
    });
};

export const isCustomAttributeCheckbox = (customAttributes, key) => {
  return customAttributes.find(attr => {
    return (
      attr.attribute_key === key && attr.attribute_display_type === 'checkbox'
    );
  });
};

export const isCustomAttributeList = (customAttributes, type) => {
  return customAttributes.find(attr => {
    return (
      attr.attribute_key === type && attr.attribute_display_type === 'list'
    );
  });
};

export const getOperatorTypes = key => {
  const operatorMap = {
    list: OPERATOR_TYPES_1,
    text: OPERATOR_TYPES_TEXT_EXTENDED,
    number: OPERATOR_TYPES_1,
    link: OPERATOR_TYPES_1,
    date: OPERATOR_TYPES_4,
    checkbox: OPERATOR_TYPES_1,
  };

  return operatorMap[key] || OPERATOR_TYPES_1;
};

export const generateCustomAttributeTypes = (customAttributes, type) => {
  return customAttributes.map(attr => {
    return {
      key: attr.attribute_key,
      name: attr.attribute_display_name,
      inputType: getCustomAttributeInputType(attr.attribute_display_type),
      filterOperators: getOperatorTypes(attr.attribute_display_type),
      customAttributeType: type,
    };
  });
};

export const generateConditionOptions = (options, key = 'id') => {
  if (!options || !Array.isArray(options)) return [];
  return options.map(i => {
    return {
      id: i[key],
      name: i.title,
    };
  });
};

export const getActionOptions = ({
  agents,
  inboxes,
  teams,
  labels,
  slaPolicies,
  type,
  addNoneToListFn,
  priorityOptions,
  actionTypes = [],
}) => {
  const actionsMap = {
    assign_agent: addNoneToListFn ? addNoneToListFn(agents) : agents,
    assign_team: addNoneToListFn ? addNoneToListFn(teams) : teams,
    send_email_to_team: teams,
    add_label: generateConditionOptions(labels, 'title'),
    remove_label: generateConditionOptions(labels, 'title'),
    change_priority: priorityOptions,
    add_sla: slaPolicies,
  };

  if (actionsMap[type]) {
    return actionsMap[type];
  }

  const optionSources = {
    agents: addNoneToListFn ? addNoneToListFn(agents) : agents,
    inboxes,
    labels: generateConditionOptions(labels, 'title'),
    priority_options: priorityOptions,
    sla_policies: slaPolicies,
    teams: addNoneToListFn ? addNoneToListFn(teams) : teams,
    teams_without_none: teams,
  };

  const actionType = actionTypes.find(action => action.key === type);
  return optionSources[actionType?.optionsSource] || [];
};

export const getConditionOptions = ({
  agents,
  availabilityStatusOptions,
  booleanFilterOptions,
  campaigns,
  contacts,
  countries,
  customAttributes,
  inboxes,
  languages,
  labels,
  statusFilterOptions,
  teams,
  type,
  priorityOptions,
  messageTypeOptions,
}) => {
  if (isCustomAttributeCheckbox(customAttributes, type)) {
    return booleanFilterOptions;
  }

  if (isCustomAttributeList(customAttributes, type)) {
    return getCustomAttributeListDropdownValues(customAttributes, type);
  }

  const conditionFilterMaps = {
    status: statusFilterOptions,
    assignee_id: agents,
    availability_status: availabilityStatusOptions,
    contact: contacts,
    inbox_id: inboxes,
    team_id: teams,
    campaigns: generateConditionOptions(campaigns),
    browser_language: languages,
    conversation_language: languages,
    country_code: countries,
    message_type: messageTypeOptions,
    priority: priorityOptions,
    labels: generateConditionOptions(labels, 'title'),
  };

  return conditionFilterMaps[type];
};

export const getFileName = (action, files = []) => {
  const blobId = action.action_params[0];
  if (!blobId) return '';
  if (action.action_name === 'send_attachment') {
    const file = files.find(item => item.blob_id === blobId);
    if (file) return file.filename.toString();
  }
  return '';
};

export const getDefaultConditions = eventName => {
  if (eventName === 'message_created') {
    return structuredClone(DEFAULT_MESSAGE_CREATED_CONDITION);
  }
  if (
    eventName === 'conversation_opened' ||
    eventName === 'conversation_resolved'
  ) {
    return structuredClone(DEFAULT_CONVERSATION_CONDITION);
  }
  if (eventName === 'contact_created' || eventName === 'contact_updated') {
    return [
      {
        attribute_key: 'email',
        filter_operator: 'equal_to',
        values: '',
        query_operator: 'and',
        custom_attribute_type: '',
      },
    ];
  }
  return structuredClone(DEFAULT_OTHER_CONDITION);
};

export const getDefaultActions = eventName => {
  const availableActionKeys = getActionKeysForEvent(eventName);
  const firstAvailableAction = availableActionKeys[0];

  const defaultParamsByAction = {
    delay_next_action: { value: '', unit: 'seconds' },
    delay_next_action_range: {
      min_value: '',
      min_unit: 'seconds',
      max_value: '',
      max_unit: 'seconds',
    },
    kanban_move_to_stage: {
      funnel_id: null,
      funnel_stage: null,
    },
  };

  if (firstAvailableAction) {
    return [
      {
        action_name: firstAvailableAction,
        action_params: defaultParamsByAction[firstAvailableAction] || [],
      },
    ];
  }

  return structuredClone(DEFAULT_ACTIONS);
};

export const filterCustomAttributes = customAttributes => {
  return customAttributes.map(attr => {
    return {
      key: attr.attribute_key,
      name: attr.attribute_display_name,
      type: attr.attribute_display_type,
    };
  });
};

export const getStandardAttributeInputType = (automationTypes, event, key) => {
  if (!automationTypes[event] || !automationTypes[event].conditions) {
    return 'plain_text';
  }

  const condition = automationTypes[event].conditions.find(
    item => item.key === key
  );
  return condition ? condition.inputType : 'plain_text';
};

export const generateAutomationPayload = payload => {
  const automation = JSON.parse(JSON.stringify(payload));
  const normalizeDelayAction = action => {
    if (action.action_name === 'delay_next_action') {
      const params = action.action_params || {};
      return {
        ...action,
        action_params: {
          ...params,
          unit: params.unit || 'seconds',
        },
      };
    }

    if (action.action_name === 'delay_next_action_range') {
      const params = action.action_params || {};
      return {
        ...action,
        action_params: {
          ...params,
          min_unit: params.min_unit || 'seconds',
          max_unit: params.max_unit || 'seconds',
        },
      };
    }

    return action;
  };

  automation.conditions[automation.conditions.length - 1].query_operator = null;
  automation.conditions = filterQueryGenerator(automation.conditions).payload;
  automation.actions = automation.actions.map(normalizeDelayAction);
  automation.actions = actionQueryGenerator(automation.actions);
  // Mark as enhanced so the backend and UI can differentiate from legacy core automations
  automation.additional_attributes = {
    ...(automation.additional_attributes || {}),
    enhanced: true,
  };
  return automation;
};

export const isCustomAttribute = (attrs, key) => {
  return attrs.find(attr => attr.key === key);
};

export const generateCustomAttributes = (
  // eslint-disable-next-line default-param-last
  conversationAttributes = [],
  // eslint-disable-next-line default-param-last
  contactAttributes = [],
  conversationlabel,
  contactlabel
) => {
  const customAttributes = [];
  if (conversationAttributes.length) {
    customAttributes.push(
      {
        key: `conversation_custom_attribute`,
        name: conversationlabel,
        disabled: true,
      },
      ...conversationAttributes
    );
  }
  if (contactAttributes.length) {
    customAttributes.push(
      {
        key: `contact_custom_attribute`,
        name: contactlabel,
        disabled: true,
      },
      ...contactAttributes
    );
  }
  return customAttributes;
};

/**
 * Get attributes for a given key from automation types.
 * @param {Object} automationTypes - Object containing automation types.
 * @param {string} key - The key to get attributes for.
 * @returns {Array} Array of condition objects for the given key.
 */
export const getAttributes = (automationTypes, key) => {
  if (
    !automationTypes ||
    !automationTypes[key] ||
    !automationTypes[key].conditions
  ) {
    return [];
  }
  return automationTypes[key].conditions;
};

/**
 * Get the automation type for a given key.
 * @param {Object} automationTypes - Object containing automation types.
 * @param {Object|string} automation - The automation object or event_name string.
 * @returns {Object} The automation type object.
 */
export const getAutomationType = (automationTypes, automation, key) => {
  // Accept either a full automation object or just the event_name string
  const eventName =
    typeof automation === 'string' ? automation : automation?.event_name;
  const eventType = automationTypes[eventName];
  if (!eventType || !eventType.conditions) {
    return null;
  }
  return eventType.conditions.find(condition => condition.key === key) || null;
};

/**
 * Get the input type for a given key.
 * @param {Array} allCustomAttributes - Array of all custom attributes.
 * @param {Object} automationTypes - Object containing automation types.
 * @param {Object} automation - The automation object.
 * @param {string} key - The key to get the input type for.
 * @returns {string} The input type.
 */
export const getInputType = (
  allCustomAttributes,
  automationTypes,
  automation,
  key
) => {
  const customAttribute = isACustomAttribute(allCustomAttributes, key);
  if (customAttribute) {
    return getCustomAttributeInputType(customAttribute.attribute_display_type);
  }
  const type = getAutomationType(automationTypes, automation, key);
  return type?.inputType || 'plain_text';
};

/**
 * Get operators for a given key.
 * @param {Array} allCustomAttributes - Array of all custom attributes.
 * @param {Object} automationTypes - Object containing automation types.
 * @param {Object|string} automation - The automation object or event_name string.
 * @param {string} key - The key to get operators for.
 * @returns {Array} Array of operators.
 */
export const getOperators = (
  allCustomAttributes,
  automationTypes,
  automation,
  key
) => {
  const customAttribute = isACustomAttribute(allCustomAttributes, key);
  if (customAttribute) {
    return getOperatorTypes(customAttribute.attribute_display_type);
  }
  const type = getAutomationType(automationTypes, automation, key);
  return type?.filterOperators || [];
};

/**
 * Get the custom attribute type for a given key.
 * @param {Object} automationTypes - Object containing automation types.
 * @param {Object} automation - The automation object.
 * @param {string} key - The key to get the custom attribute type for.
 * @returns {string} The custom attribute type.
 */
export const getCustomAttributeType = (automationTypes, automation, key) => {
  if (
    !automationTypes ||
    !automation?.event_name ||
    !automationTypes[automation.event_name]
  ) {
    return null;
  }
  const condition = automationTypes[automation.event_name].conditions?.find(
    i => i.key === key
  );
  return condition?.customAttributeType || null;
};

/**
 * Determine if an action input should be shown.
 * @param {Array} automationActionTypes - Array of automation action type objects.
 * @param {string} action - The action to check.
 * @returns {boolean} True if the action input should be shown, false otherwise.
 */
export const showActionInput = (automationActionTypes, action) => {
  if (action === 'send_email_to_team' || action === 'send_message')
    return false;
  const type = automationActionTypes.find(i => i.key === action)?.inputType;
  return !!type;
};
